import {
  format,
  parseISO,
} from 'date-fns';
import { object } from 'yup';
import { objectIsValid } from '@mgo/core/src/validations/utils';
import { attachFieldErrors } from '@mgo/core/src/validations/stringValidator';
import { formatZip9 } from '../../utils/zip9';
import pageView from '../../mixins/pageView';
import validators from './validations';
import { redirectToHomePage } from '../../utils/commonUtil';

export default {
  mixins: [pageView],
  data() {
    return {
      fieldErrors: [],
      improvedFieldErrors: {},
      coa: {},
      bufferedStartDate: '',
      lazy: true,

      // COA properties
      billingAddressType: '',
      dcoa: false,
      forwardType: '',
      informedDelivery: false,
      moverType: '',
      startDate: '',
      stopDate: '',
      sdBuffered: false,
      mobilePhone: '',

      // preparer contact data
      preparerType: 'MOVER',
      preparerFirstName: '',
      preparerMiddleName: '',
      preparerLastName: '',
      preparerEmail: '',
      preparerPhoneNumber: '',
      preparerPhoneType: 'MOBILE',
      preparerUUID: '',

      // Address Flags
      thirdAddressEligible: false,
      oldAddress: {},
      newAddress: {},

      // other/third address
      billingAddress: {},
      billingStreetAddress: '',
      billingCity: '',
      billingState: '',
      billingZipCode: '',
      billingZip9: '',
      billingUrbanizationName: '',
      billingPoBoxNumber: '',
      billingPrivateMailbox: '',
      billingAddressUUID: '',

      // Extended Mail Forwarding
      emf: '',
      extensions: [],

      // Business Movers
      aliases: [],
      business: [],
      icoaAmount: 0,

      // IVS verification modal
      showMfaModal: false,
    };
  },
  computed: {
    showSimplifyExperience: () => JSON.parse(sessionStorage.getItem('showSimplifyExperience') ?? false),
    combinedErrors() {
      return { ...this.errors, ...this.improvedFieldErrors };
    },
    mixCaseOldAddress() {
      const street = this.toTitleCase(this.oldAddress.amsCompleteAddress);
      const city = this.toTitleCase(this.oldAddress.amsCity);
      const partial = [street, city, this.oldAddress.amsState].join(', ');
      return `${partial} ${this.oldAddress.amsZip5}`;
    },
    mixCaseNewAddress() {
      const street = this.toTitleCase(this.newAddress.amsCompleteAddress);
      const city = this.toTitleCase(this.newAddress.amsCity);
      const partial = [street, city, this.newAddress.amsState].join(', ');
      return `${partial} ${this.newAddress.amsZip5}`;
    },
    showNonControlExperience: () => JSON.parse(sessionStorage.getItem('showNonControlExperience')),
    showChoiceExperience: () => JSON.parse(sessionStorage.getItem('showChoiceExperience')),
    extensionSelected() {
      return JSON.parse(sessionStorage.getItem('extensionSelected')) ?? 0;
    },
    movingFromPoBox() {
      return !!this.oldAddress?.poBoxNumber || false;
    },
    oldAddressBusiness() {
      return this.oldAddress?.business || false;
    },
    newAddressBusiness() {
      return this.newAddress?.business || false;
    },
    newAddressCmra() {
      return this.newAddress?.cmra || false;
    },
    newAddressDpo() {
      return this.newAddress?.dpo || false;
    },
    newAddressInUniqueZip() {
      return this.newAddress?.inUniqueZip || false;
    },
    newAddressMilitary() {
      return this.newAddress?.military || false;
    },
    isPermanent() {
      return this.forwardType === 'PERMANENT';
    },
    hrOldAddress() {
      if (!Object.keys(this.oldAddress).length) return '';
      return `${this.oldAddress?.amsCompleteAddress}, ${this.oldAddress?.amsCity} ${this.oldAddress?.amsState} ${this.formatZip9(this.oldAddress?.amsZip9)}`;
    },
    hrNewAddress() {
      if (!Object.keys(this.newAddress).length) return '';
      return `${this.newAddress?.amsCompleteAddress}, ${this.newAddress?.amsCity} ${this.newAddress?.amsState} ${this.formatZip9(this.newAddress?.amsZip9)}`;
    },
    hrBillingAddress() {
      if (!Object.keys(this.billingAddress).length) return '';
      return `${this.billingAddress?.amsCompleteAddress}, ${this.billingAddress?.amsCity} ${this.billingAddress?.amsState} ${this.formatZip9(this.billingAddress?.amsZip9)}`;
    },
    hrMcoaAddress() {
      return {
        line1: this.oldAddress?.amsCompleteAddress,
        line2: `${this.oldAddress?.amsCity}, ${this.oldAddress?.amsState} ${this.formatZip9(this.oldAddress?.amsZip9)}`,
      };
    },
    billingAddressComplete() {
      return Boolean(this.billingStreetAddress
        && this.billingCity
        && this.billingState
        && this.billingZipCode);
    },
    flagBizBillingAddressEnabled() {
      return this.$store.get('flagBizBillingAddressEnabled');
    },
    flagEMFEnabled() {
      return this.$store.get('flagEMFEnabled');
    },
    bizCostComputationEnabled() {
      return this.$store.get('config@bizCostComputationEnabled');
    },
    businessToMilitary() {
      return (
        this.moverType === 'BUSINESS'
        && this.oldAddressBusiness
        && this.newAddressMilitary
      );
    },
    emfProperties() {
      if (!this.emf) return {};

      return this.extensions?.find(({ optionId }) => optionId.toString() === this.emf)
      || { optionCost: '0', optionPeriod: 0 };
    },
    icoaAmountFormatted() {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
      }).format(this.icoaAmount / 100);
    },
    formIsValid() {
      return objectIsValid(this.errors);
    },
    informedDeliveryEligible() {
      return (
        this.moverType
        && this.moverType !== 'BUSINESS'
        && this.forwardType === 'PERMANENT'
        && this.billingAddressType !== 'OTHER'
      );
    },
    rules() {
      return object().shape(validators);
    },
    showThirdAddressOption() {
      if (this.moverType === 'BUSINESS' && this.oldAddressBusiness && this.newAddressBusiness && this.isPermanent) {
        return true;
      }

      if (this.flagBizBillingAddressEnabled && this.isPermanent) {
        return this.thirdAddressEligible;
      }

      return this.isPermanent && this.moverType !== 'BUSINESS';
    },
  },
  async mounted() {
    if (!this.$coa.coaUUID) {
      redirectToHomePage(this);
      return;
    }

    // Rehydrate buffer datepicker when returning from payment
    if (this.$route.query.sdbuf) {
      this.bufferedStartDate = this.startDate;
    }

    this.$store.commit('ENABLE_LOADER');

    await this.hydrateCOA();
    this.getExtensions();

    if (this.coa.multipleAddresses) {
      await this.hydrateBusiness();
    }

    /*
     * redirect back to disclaimer
     * if base data (startDate) isn't available
     * or coa complete is true
     */
    if (!this.coa.startDate || this.coa.complete) {
      this.$router.push({ name: 'disclaimer' });
      this.$store.commit('DISABLE_LOADER');
      return;
    }

    await this.hydrateAddresses();
    await this.hydratePreparer();

    if (this.moverType === 'BUSINESS') {
      const { aliases } = await this.$coa.businessAliasGetAll();
      this.aliases = aliases || [];
    }

    this.icoaAmount = await this.calCoaAmount();

    this.bufferedStartDate = format(this.minBufferedStartDate, 'yyyy-MM-dd');

    this.$store.commit('DISABLE_LOADER');
    this.pageView();

    this.clearMfaSessionStore();
    if (this.isMfaEnabled()) {
      await this.initiateMfa();
    } else {
      // stops polling on page-return if it has been initiated
      await this.$store.dispatch('mfa/stopValidatingLink');
    }
  },
  methods: {
    mapMfaErrors(...error) {
      const err = this.$store.get('mfa/error').exception;
      if (err === 'UnverifiedPhoneException' || err === 'IvsValidationException') {
        this.$router.push({ name: 'identity-check' });
      }
      return error.includes(err);
    },
    mfaError(error) {
      return this.$store.get('mfa/error').exception === error;
    },
    patchInformedDelivery() {
      if (/new|old/i.test(this.billingAddressType)) {
        this.informedDelivery = JSON.parse(sessionStorage.getItem('informedDelivery') ?? false);
      }
    },
    prepareFieldErrors({ raw, xhr }) {
      if (!raw && !xhr) return null;
      const { personType } = JSON.parse(raw.config.data);
      if (/PREPARER/g.test(personType)) {
        const obj = xhr.fieldErrors.reduce((accum, { field, message }) => {
          const key = `preparer${field[0].toUpperCase()}${field.slice(1)}`;
          if (field === 'mobilePhone' || field === 'homePhone') {
            Object.assign(accum, { preparerPhoneNumber: { message, hasError: true } });
          } else {
            Object.assign(accum, { [key]: { hasError: true, message } });
          }
          return accum;
        }, {});
        this.improvedFieldErrors = { ...this.improvedFieldErrors, ...obj };
      }
      return this.improvedFieldErrors;
    },
    clearErrors(fieldName) {
      this.fieldErrors.forEach((fieldError, index) => {
        if (fieldError.field.indexOf(fieldName) === 0) {
          this.fieldErrors.splice(index, 1);
        }
      });
    },
    mixCaseBillingAddress() {
      if (!this.billingAddressComplete) return '';
      const street = this.toTitleCase(this.billingAddress.amsCompleteAddress);
      const city = this.toTitleCase(this.billingAddress.amsCity);
      const partial = [street, city, this.billingAddress.amsState].join(', ');
      return `${partial} ${this.billingAddress.amsZip5}`;
    },
    toTitleCase(str) {
      if (str) {
        return str.toLowerCase()
          .split(' ')
          .map((char) => (char ? `${char[0]?.toUpperCase()}${char.slice(1)}` : ''))
          .join(' ');
      }
      return '';
    },
    formatZip9,
    attachFieldErrors,
    async getExtensions() {
      // prevent EMF if disabled
      if (!this.flagEMFEnabled || this.moverType === 'TEMPORARY') return;

      const { eligible } = await this.$coa.extensionPrescreen({
        orderUuid: this.$coa.coaUUID,
        forwardType: this.coa?.forwardType,
        dcoa: this.coa?.dcoa,
        mcoa: this.coa?.mcoa,
        oldZip5: this.oldAddress?.amsZip5,
        newZip5: this.newAddress?.amsZip5,
      });

      if (eligible) {
        const {
          availableExtensions,
        } = await this.$coa.extensionGetAll(this.coa?.visitId);

        // capture the extension return
        this.extensions = availableExtensions || [];
      }
    },
    /**
     * Get formatted date string
     * @param {String} date
     * @param {String} formatting - ex. 'dd/MM/yyyy'
     */
    getFormattedDate(date, formatting) {
      if (typeof date === 'string') {
        return format(parseISO(date), formatting);
      }
      return format(date, formatting);
    },
    async hydrateCOA() {
      const { uuid, ...coa } = await this.$coa.coaGet() || {};

      if (uuid) {
        this.billingAddressType = coa.billingAddressType || 'OLD';
        this.dcoa = coa.dcoa;
        this.forwardType = coa.forwardType;
        this.informedDelivery = coa.informedDelivery;
        this.moverType = coa.moverType;
        this.startDate = coa.startDate;
        this.stopDate = coa.stopDate;
        this.sdBuffered = coa.sdBuffered;
      }

      sessionStorage.setItem('mgoExitSurveyActive', this.$store.get('config').mgoExitSurveyActive);

      this.coa = { ...coa };
    },
    getAddressString({
      streetAddress,
      city,
      state,
      zipCode,
      urbanizationName,
      poBoxNumber,
      privateMailbox,
    }) {
      return JSON.stringify({
        streetAddress,
        city,
        state,
        zipCode,
        urbanizationName,
        poBoxNumber,
        privateMailbox,
      }).toLowerCase();
    },
    async hydrateBusiness() {
      this.business = await this.$coa.businessGetAll();
    },


    async calCoaAmount() {
      let computedAmount = 0;
      if (this.moverType === 'BUSINESS' && this.coa.multipleAddresses && this.bizCostComputationEnabled) {
        if (this.business.length) {
          computedAmount += (
            this.business.filter((x) => !x.dcoa).length
            * this.$store.get('icoaAmount'));

          const aliasHandlers = [];
          this.business.forEach((b) => {
            if (!b.dcoa) {
              aliasHandlers.push(this.$coa.businessAliasGetAllByOrderUuid(b.uuid));
            }
          });

          const aliasResponses = await Promise.all(aliasHandlers);
          aliasResponses.forEach((aliasResponse) => {
            const { aliases } = aliasResponse;
            if (aliases?.length) {
              computedAmount += (aliases.length * this.$store.get('icoaAmount'));
            }
          });
        }
      } else {
        computedAmount = this.$store.get('icoaAmount')
        // Add in Biz Alias amounts
        + (this.bizCostComputationEnabled
          ? (this.$store.get('icoaAmount') * this.aliases.length)
          : 0);
      }

      return computedAmount;
    },
    async hydrateAddresses() {
      if (this.flagBizBillingAddressEnabled) {
        const {
          oldAddress, newAddress, billingAddress, thirdAddressEligible,
        } = await this.$coa.addressGetAllV2();
        this.oldAddress = oldAddress || {};
        this.newAddress = newAddress || {};
        this.billingAddress = {};

        if (billingAddress
            && (this.getAddressString(billingAddress) !== this.getAddressString(oldAddress))
            && (this.getAddressString(billingAddress) !== this.getAddressString(newAddress))) {
          this.billingAddress = billingAddress;
        }

        this.thirdAddressEligible = thirdAddressEligible || false;
      } else {
        const { addresses } = await this.$coa.addressGetAll();
        const oldAddress = addresses.find(({ addressType }) => addressType === 'OLD');
        const newAddress = addresses.find(({ addressType }) => addressType === 'NEW');
        const billingAddress = addresses.find(({ addressType }) => addressType === 'BILLING');
        this.oldAddress = oldAddress || {};
        this.newAddress = newAddress || {};
        this.billingAddress = {};

        if (billingAddress
            && (this.getAddressString(billingAddress) !== this.getAddressString(oldAddress))
            && (this.getAddressString(billingAddress) !== this.getAddressString(newAddress))) {
          this.billingAddress = billingAddress;
        }
      }

      if (Object.keys(this.billingAddress).length > 0) {
        this.billingStreetAddress = this.billingAddress.streetAddress;
        this.billingCity = this.billingAddress.city;
        this.billingState = this.billingAddress.state;
        this.billingZipCode = this.billingAddress.zipCode;
        this.billingUrbanizationName = this.billingAddress.urbanizationName;
        this.billingPrivateMailbox = this.billingAddress.privateMailbox;
        this.billingPoBoxNumber = this.billingAddress.poBoxNumber;
        this.billingAddressUUID = this.billingAddress?.uuid || '';
      }
    },
    async hydratePreparer() {
      const { PREPARER, MOVER } = await this.$coa.personGetAll();
      if (PREPARER) {
        const {
          firstName, middleName, lastName, suffix, email, homePhone, mobilePhone, uuid, personType,
        } = PREPARER;
        this.preparerType = personType;
        this.preparerFirstName = firstName;
        this.preparerMiddleName = middleName;
        this.preparerLastName = lastName;
        this.preparerSuffix = suffix;
        this.preparerEmail = email;
        this.preparerPhoneNumber = mobilePhone || homePhone;
        this.preparerPhoneType = mobilePhone ? 'MOBILE' : 'HOME';
        this.preparerUUID = uuid ?? '';
      }
      if (MOVER) {
        this.mobilePhone = MOVER.mobilePhone;
      }
    },
    async scrollToFirstError() {
      const errorHTML = document.querySelector('.errors');
      if (errorHTML !== null) {
        setTimeout(() => {
          errorHTML.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }, 100);
      }
    },
    async next(exception) {
      /* eslint-disable camelcase */

      if (
        this.isMfaEnabled()
        && !this.$store.getters['mfa/canProceedToPayment']
      ) {
        this.$store.commit('DISABLE_LOADER');
        this.showIVSVerificationModal();
        return;
      }

      const utmParams = {
        utm_campaign: this.$route?.query?.utm_campaign || '',
        utm_medium: this.$route?.query?.utm_medium || '',
        utm_source: this.$route?.query?.utm_source || '',
      };
      /* eslint-enable camelcase */
      const suffixUrl = Object.keys(utmParams)
        .filter((key) => utmParams[key])
        .reduce((acc, key) => `${acc}&${key}=${utmParams[key]}`, '');
      const bufferParam = this.bufferStartDate ? '?sdbuf=true' : '';
      const returnUrl = `${window.location.origin}${this.$route.path}${bufferParam}${suffixUrl}`;
      const { redirectUrl } = await this.$coa.globalPaymentURLGet(returnUrl, suffixUrl);

      if (!redirectUrl || exception) {
        this.$store.commit('DISABLE_LOADER');
        return;
      }

      this.toPayment(redirectUrl);
    },
    toPayment(redirectUrl) {
      this.pageView('payment');
      window.location = redirectUrl;
    },
    async initiateMfa() {
      if (this.coa?.ivsProofingCompleted) {
        if (this.coa?.ivsVerificationPassed) {
          this.$store.commit('mfa/SET_IVS_VERIFICATION_STATUS', this.coa.ivsVerificationPassed);
        } else {
          this.$store.commit('mfa/SET_ALLOW_IPP', true);
        }
      } else {
        this.$store.commit('ENABLE_LOADER');
        await this.$store.dispatch('mfa/identityCheck');
        this.$store.commit('DISABLE_LOADER');
        if (!this.$store.get('mfa/ivsVerificationPassed')) {
          if (this.$store.getters['mfa/hasError']) {
            const { exception, message } = this.$store.get('mfa/error');

            window.console.log(`identityCheck error - exception:${exception} message:${message}`);

            // Error message display is handled in info-panel within the template
            if (exception === 'DeviceRejectedException' || exception === 'PhoneVerifiedFailedException') {
              window.console.log('no message display in this case');
            }
            if (exception === 'VerificationLockedException') {
              window.console.log('display lock exception message');
            }
            if (exception === 'SystemErrorException' || exception === 'IvsValidationException') {
              window.console.log('display error message on screen for SystemErrorException');
            }

            return;// skip mfa modals
          }
          this.showIVSVerificationModal();
        }
      }
    },
    showIVSVerificationModal() {
      if (!this.$store.getters['mfa/canProceedToPayment']) {
        this.showMfaModal = true;
      }
    },
    clearMfaSessionStore() {
      // reset mfa state
      this.$store.commit('mfa/SET_IVS_VERIFICATION_STATUS', false);
      this.$store.commit('mfa/SET_ALLOW_IPP', false);
      this.$store.commit('mfa/SET_IS_OTP', false);

      // clear session variables
      sessionStorage.removeItem('optIntoIPP');
      sessionStorage.removeItem('passedIvsVerification');
      sessionStorage.removeItem('ippRegistrationSuccess');
    },
    isMilitary() {
      return this.oldAddress?.military || this.newAddress?.military
      || this.oldAddress?.militaryZipCode || this.newAddress?.militaryZipCode;
    },
    isMfaEnabled() {
      return this.moverType !== 'BUSINESS'
      && !this.isMilitary()
      && this.$store.get('config@twoFactorAuthEnabled');
    },
  },
};
